/**
 * A finished translation: original and translation side by side (open in a
 * new tab / download, both through the audited content route), page count,
 * characters translated, languages, whether the collection is indexing it —
 * under the machine-translation advisory (CLAUDE.md §9.6b).
 */

import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconDownload, IconExternalLink } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  AdvisoryBanner,
  ReportDownloadButton,
  useDocumentViewer,
} from "@/features/solutions/shared";
import { indexDocumentContentUrl } from "@/shared/api/indexes";
import { downloadFile } from "@/shared/api/integration-browse";
import type { RunDetailOut } from "@/shared/api/solutionRuns";
import { toastApiError } from "@/shared/ui/toast";
import { formatBytes, type TranslatedDocument, type TranslateResult } from "./translateMeta";

function DocumentCard({
  heading,
  indexId,
  doc,
  language,
}: {
  heading: string;
  indexId: string;
  doc: TranslatedDocument;
  language: string;
}) {
  const getToken = useAccessToken();
  const viewer = useDocumentViewer();
  const [busy, setBusy] = useState<"open" | "download" | null>(null);
  const url = indexDocumentContentUrl(indexId, doc.document_id);

  const act = async (what: "open" | "download") => {
    setBusy(what);
    try {
      if (what === "open" && viewer) viewer.openDocument({ path: url, name: doc.display_name });
      else await downloadFile(await getToken(), url, doc.display_name);
    } catch (err) {
      toastApiError("Could not open the document", err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper withBorder radius="md" p="md" data-testid={`translate-${heading.toLowerCase()}`}>
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">
            {heading}
          </Text>
          <Badge variant="light">{language}</Badge>
        </Group>
        <Text fw={600} style={{ wordBreak: "break-word" }}>
          {doc.display_name}
        </Text>
        <Text size="sm" c="dimmed">
          {doc.page_count != null ? `${doc.page_count} pages · ` : ""}
          {formatBytes(doc.size_bytes)}
        </Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconExternalLink size={14} />}
            loading={busy === "open"}
            onClick={() => void act("open")}
            aria-label={`Open ${heading.toLowerCase()} in a new tab`}
          >
            Open
          </Button>
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconDownload size={14} />}
            loading={busy === "download"}
            onClick={() => void act("download")}
            aria-label={`Download ${heading.toLowerCase()}`}
          >
            Download
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

export function TranslateResultView({
  run,
  result,
}: {
  run: RunDetailOut;
  result: TranslateResult;
}) {
  const from = result.source_language ? result.source_language.toUpperCase() : "Auto-detected";
  const to = result.target_language.toUpperCase();
  return (
    <Stack gap="md" data-testid="translate-result">
      <AdvisoryBanner variant="machine-translation" />
      <Group justify="space-between">
        <Title order={4}>{run.name}</Title>
        <ReportDownloadButton run={run} />
      </Group>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <DocumentCard
          heading="Original"
          indexId={result.index_id}
          doc={result.original}
          language={from}
        />
        <DocumentCard
          heading="Translation"
          indexId={result.index_id}
          doc={result.translation}
          language={to}
        />
      </SimpleGrid>
      <Table verticalSpacing={4} withRowBorders={false}>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td c="dimmed">Languages</Table.Td>
            <Table.Td>
              {from} → {to}
            </Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c="dimmed">Characters translated</Table.Td>
            <Table.Td>{result.characters_charged.toLocaleString()}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c="dimmed">Collection</Table.Td>
            <Table.Td>{result.index_name}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c="dimmed">Service</Table.Td>
            <Table.Td>
              {result.service} · API {result.api_version}
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
      {result.replaced_previous && (
        <Text size="sm" c="dimmed">
          This replaced the collection&apos;s earlier {to} translation of the document.
        </Text>
      )}
      <Alert
        variant="light"
        color={result.build.status === "started" ? "blue" : "yellow"}
        title="Search"
      >
        {result.build.message}
      </Alert>
    </Stack>
  );
}
